import React, { Fragment, ReactNode } from 'react';
import { OptInMessage } from 'features/auth/types';

interface ResolveOptInMessageArgs {
  message: OptInMessage | undefined;
  locale: string;
  applicationName: string;
  privacyPolicyUrl?: string;
  termsOfServiceUrl?: string;
  privacyPolicyLabel: string;
  termsOfServiceLabel: string;
}

/**
 * Resolves a localized opt-in message based on the user's locale.
 * Falls back to English if the specific locale is unavailable.
 *
 * Replaces placeholders in the resolved message:
 * - {app_name_placeholder}         -> cobrand application name (plain text)
 * - {privacy_policy_placeholder}   -> clickable privacy policy link
 * - {terms_of_service_placeholder} -> clickable terms of service link
 */
export const resolveOptInMessage = ({
  message,
  locale,
  applicationName,
  privacyPolicyUrl,
  termsOfServiceUrl,
  privacyPolicyLabel,
  termsOfServiceLabel,
}: ResolveOptInMessageArgs): ReactNode | undefined => {
  if (!message) return undefined;

  const language = locale.split('-')[0].toLowerCase() as keyof OptInMessage;
  const resolved = message[language] ?? message.en ?? undefined;

  if (!resolved) return undefined;

  const withAppName = resolved.split('{app_name_placeholder}').join(applicationName);

  return withAppName
    .split(/(\{privacy_policy_placeholder\}|\{terms_of_service_placeholder\})/g)
    .map((part, index) => {
      if (part === '{privacy_policy_placeholder}') {
        return (
          <a
            key={`privacy-${index}`}
            href={privacyPolicyUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {privacyPolicyLabel}
          </a>
        );
      }
      if (part === '{terms_of_service_placeholder}') {
        return (
          <a
            key={`terms-${index}`}
            href={termsOfServiceUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {termsOfServiceLabel}
          </a>
        );
      }
      return <Fragment key={`text-${index}`}>{part}</Fragment>;
    });
};
